import { motion } from 'motion/react'
import { Check, X as XIcon, Shield, HardDrive, Gamepad2, Clock } from 'lucide-react'
import { MarketingLayout } from '../components/MarketingLayout'
import { SEO } from '../components/SEO'
import { ComparisonTable } from '../components/ComparisonTable'
import { FAQ } from '../components/FAQ'
import { CTABanner } from '../components/CTABanner'
import { FeatureGrid } from '../components/FeatureCard'

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary'

const faqs = [
  {
    q: '¿Voltary es más caro que el hosting mensual de Minecraft?',
    a: 'Depende del uso. Si tu servidor está encendido 24/7 todo el mes, las suscripciones mensuales baratas suelen ganar en precio puro: $3-15/mes por 4 GB. Pero Voltary cobra $0.150/hora y el cobro se interrumpe al detener la sesión. Si solo juegas unas horas a la semana, el precio mensual efectivo de Voltary puede quedar en $2-5/mes, muy por debajo de cualquier suscripción.',
  },
  {
    q: '¿Voltary tiene planes mensuales?',
    a: 'No. Voltary es pago por horas, sin permanencia ni contratos. Sin embargo en un futuro cercano añadiremos planes mensuales opcionales para quienes quieran pagar por adelantado y obtener descuento.',
  },
  {
    q: '¿Cuánto cuesta un servidor 24/7 con Voltary?',
    a: 'El Plan Básico (4 GB, 2 núcleos, 40 GB SSD) en Alemania sale $0.150/hora. Encendido 24/7 todo el mes: $0.150 × 24 × 30 = $108/mes. En Estados Unidos ($0.180/hora) son $129.60/mes. Si tu servidor es para uso 24/7 comunitario, las suscripciones mensuales son más baratas en precio base. Si tu servidor es para jugar cuando te apetezca, Voltary es imbatible.',
  },
  {
    q: '¿Cuánto cuesta un servidor que solo uso los fines de semana?',
    a: 'Si juegas 4 horas cada sábado y domingo (8 horas/mes totales), el Plan Básico de Voltary cuesta $0.150 × 8 = $1.20/mes. La misma máquina en cualquier suscripción mensual cobra el precio completo ($3-15/mes) aunque no lauses. Para uso intermitente Voltary gana 3-12x en precio.',
  },
  {
    q: '¿Por qué la gente paga suscripciones mensuales si Voltary es más barato en uso intermitente?',
    a: 'Suele suceder por varias razones, como lo serían: (1) para servidores 24/7 comunitarios, las suscripciones son más baratas; (2) muchos usuarios no saben que existe el modelo por horas y compran lo que conocen. Si tu caso es de uso intermitente, vale la pena probar Voltary antes de pagar 12 meses por adelantado.',
  },
  {
    q: '¿Voltary soporta modpacks como los servicios mensuales?',
    a: 'Sí. Subes modpacks por FTP a la carpeta /mods (compatible con CurseForge y Modrinth). El panel web permite instalar mods individuales pero modpacks completos aún no.',
  },
  {
    q: '¿Voltary tiene permanencia mínima o contratos?',
    a: 'No. Recargas saldo y juegas. Si te quedas sin saldo, el servidor se apaga. No hay contratos, no hay permanencia, no hay penalización por cancelar. Es lo opuesto a las suscripciones que suelen pedir compromiso de 3-12 meses para ofrecer descuento.',
  },
]

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${APP_NAME} vs Otros hostings — Comparativa 2026`,
    description: 'Comparamos el modelo de pago por horas de Voltary contra las suscripciones mensuales de hosting de Minecraft. Cuándo gana cada uno según tu patrón de uso.',
    author: { '@type': 'Organization', name: APP_NAME },
    publisher: {
      '@type': 'Organization',
      name: APP_NAME,
      logo: { '@type': 'ImageObject', url: 'https://voltary.org/og.png' },
    },
    datePublished: '2026-08-14',
    inLanguage: 'es-ES',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://voltary.org/' },
      { '@type': 'ListItem', position: 2, name: 'Comparativas', item: 'https://voltary.org/blog' },
      { '@type': 'ListItem', position: 3, name: 'vs Hosting mensual' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
]

export function VSMonthlyHosting() {
  return (
    <MarketingLayout>
      <SEO
        title={`${APP_NAME} vs Otros hostings — Comparativa 2026`}
        description="Voltary cobra por horas; los servicios mensuales de hosting de Minecraft cobran suscripción. Cuándo gana cada modelo según si tu servidor está 24/7 o solo cuando juegas."
        canonical="https://voltary.org/vs/hosting-mensual"
        jsonLd={jsonLd}
      />

      <article className="marketing-article">
        <motion.span
          className="marketing-article-eyebrow"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          Comparativa 2026
        </motion.span>

        <motion.h1
          className="marketing-article-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {APP_NAME} vs Otros hostings
        </motion.h1>

        <motion.p
          className="marketing-article-lead"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <strong>No es blanco o negro.</strong> Hay un ganador distinto según
          uses tu servidor 24/7 o solo cuando juegas. Esta página compara
          el modelo de pago por horas de {APP_NAME} contra el modelo clásico
          de suscripción mensual para que decidas según tu caso real.
        </motion.p>

        <h2>Tabla comparativa (datos a agosto 2026)</h2>
        <p>
          Los precios de "hosting mensual" son rangos típicos del mercado
          basados en muestreo de los principales proveedores. En suscripciones
          mensuales el precio mostrado es la cuota recurrente (sin contar
          descuentos por compromiso de 12+ meses).
        </p>

        <ComparisonTable
          competitors={['Hosting mensual típico', APP_NAME]}
          rows={[
            { feature: 'Modelo de cobro', 'Hosting mensual típico': 'Cuota fija al mes, pagues o no', [APP_NAME]: 'Por horas, solo cuando el server está encendido' },
            { feature: 'Precio 4 GB 8h/semana', 'Hosting mensual típico': '$3-15/mes (pagas cuota completa)', [APP_NAME]: '~$1.20-5/mes (solo horas activas)' },
            { feature: 'Almacenamiento SSD', 'Hosting mensual típico': '10-30 GB típico', [APP_NAME]: '40 GB incluido (hasta 320 GB)' },
            { feature: 'Anti-DDoS', 'Hosting mensual típico': 'Incluido en la mayoría', [APP_NAME]: 'Incluido' },
            { feature: 'Cambio de plan', 'Hosting mensual típico': 'Sí, generalmente inmediato', [APP_NAME]: 'Sí, sin permanencia' },
            { feature: 'Permanencia / contratos', 'Hosting mensual típico': 'Sin permanencia, descuentos por 3-12 meses', [APP_NAME]: 'Sin permanencia, sin descuentos por compromiso' },
            { feature: 'Aislamiento entre clientes', 'Hosting mensual típico': 'Varios clientes comparten el mismo servidor físico', [APP_NAME]: 'Cada cliente tiene su propio servidor independiente' },
            { feature: 'Modpacks (instalación)', 'Hosting mensual típico': 'Un click desde el panel', [APP_NAME]: 'Por FTP (compatible con CurseForge y Modrinth)' },
            { feature: 'Juegos soportados', 'Hosting mensual típico': 'Solo Minecraft (mayoría)', [APP_NAME]: 'Minecraft, Counter-Strike 1.6, Counter-Strike: Source, Terraria' },
            { feature: 'Migrar desde otro servicio', 'Hosting mensual típico': 'Asistente propio o soporte', [APP_NAME]: 'Por FTP, ~5 minutos' },
            { feature: 'Soporte técnico', 'Hosting mensual típico': '24/7 por ticket o Discord (varía)', [APP_NAME]: 'Discord directo con el equipo técnico' },
            { feature: 'Panel de control', 'Hosting mensual típico': 'Multicraft muy pulido, 10+ años de desarrollo', [APP_NAME]: 'Panel propio en evolución' },
          ]}
        />

        <h2>El veredicto según tu caso</h2>

        <h3>Gana la suscripción mensual si…</h3>
        <ul>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Tu servidor va a estar encendido 24/7 todo el mes para tu comunidad.
          </li>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Quieres un panel tipo Multicraft con décadas de pulido y soporte 24/7 humano.
          </li>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Estás dispuesto a firmar 6-12 meses a cambio de descuentos del 10-20%.
          </li>
        </ul>

        <h3>Gana {APP_NAME} si…</h3>
        <ul>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Solo enciendes el server cuando juegas (fines de semana, tardes, etc.).
          </li>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Odias pagar por algo que no estás usando.
          </li>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Quieres jugar a varios juegos (Minecraft + Counter-Strike + Terraria) en una sola plataforma.
          </li>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Necesitas más de 30 GB de almacenamiento para mundos grandes o backups.
          </li>
          <li>
            <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> Prefieres que tu servidor esté separado físicamente de los demás clientes.
          </li>
        </ul>

        <h2>Ejemplo real: caso "uso intermitente"</h2>

        <FeatureGrid
          items={[
            {
              icon: <Clock size={24} />,
              title: '8 horas al mes (4h × 2 días)',
              description: (
                <>
                  <strong>Voltary Plan Básico DE:</strong> $0.150 × 8 = <strong>$1.20/mes</strong>
                  <br />
                  <strong>Hosting mensual barato:</strong> ~$5/mes (cuota completa)
                  <br />
                  <strong>Ahorro con Voltary:</strong> ~76%
                </>
              ),
            },
            {
              icon: <Clock size={24} />,
              title: '32 horas al mes (8h × 4 días)',
              description: (
                <>
                  <strong>Voltary Plan Básico DE:</strong> $0.150 × 32 = <strong>$4.80/mes</strong>
                  <br />
                  <strong>Hosting mensual mid:</strong> ~$14/mes
                  <br />
                  <strong>Ahorro con Voltary:</strong> ~66%
                </>
              ),
            },
            {
              icon: <Clock size={24} />,
              title: '120 horas al mes (4h × 30 días)',
              description: (
                <>
                  <strong>Voltary Plan Básico DE:</strong> $0.150 × 120 = <strong>$18/mes</strong>
                  <br />
                  <strong>Hosting mensual mid:</strong> ~$14/mes
                  <br />
                  <strong>Ahorro con suscripción:</strong> ~22%
                </>
              ),
            },
            {
              icon: <Clock size={24} />,
              title: '720 horas (24/7 todo el mes)',
              description: (
                <>
                  <strong>Voltary Plan Básico DE:</strong> $0.150 × 720 = <strong>$108/mes</strong>
                  <br />
                  <strong>Hosting mensual barato:</strong> ~$5-10/mes
                  <br />
                  <strong>Ahorro con suscripción:</strong> 90%+
                </>
              ),
            },
          ]}
        />

        <h2>Qué ofrece {APP_NAME} que no encontrarás en suscripciones mensuales</h2>

        <FeatureGrid
          items={[
            {
              icon: <Shield size={24} />,
              title: 'Servidor solo para ti',
              description: 'Tu servidor corre en su propia máquina virtual. El hosting mensual típico mete varios clientes en el mismo servidor físico: si un vecino tiene un pico de uso, te afecta a ti.',
            },
            {
              icon: <HardDrive size={24} />,
              title: 'Más almacenamiento SSD',
              description: '40-320 GB incluidos en planes. El hosting mensual típico limita a 10-30 GB; para mundos grandes con mods o backups, te quedas corto rápido.',
            },
            {
              icon: <Gamepad2 size={24} />,
              title: 'Multi-juego bajo una sola cuenta',
              description: 'La misma cuenta, mismo saldo, mismo panel. Creas un servidor de Minecraft, luego uno de Counter-Strike, luego Terraria. El hosting mensual típico se ata a un solo juego (Minecraft).',
            },
            {
              icon: <Clock size={24} />,
              title: 'Pago por hora real',
              description: 'Apagas el servidor cuando no juegas y el cobro por hora se detiene. En suscripciones mensuales pagas la cuota completa aunque tengas el server apagado una semana.',
            },
          ]}
        />

        <h2>Veredicto</h2>
        <p>
          <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> <strong>Suscripciones mensuales</strong> ganan en precio puro para uso 24/7 comunitario y ofrecen paneles Multicraft muy pulidos.
        </p>
        <p>
          <Check size={16} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> <strong>{APP_NAME}</strong> gana en uso intermitente, multi-juego, servidor aislado y almacenamiento. Si solo enciendes cuando juegas, sale entre 70-95% más barato.
        </p>
        <p>
          <XIcon size={16} style={{ verticalAlign: 'middle', color: 'var(--error)' }} /> En ambos casos pagas por infraestructura compartida: en Voltary tu servidor está separado del de otros clientes, pero los recursos de la máquina los comparten varios VPS. Las suscripciones mensuales agrupan más clientes en la misma máquina física.
        </p>

        <FAQ items={faqs} title="Preguntas frecuentes sobre hosting por horas vs mensual" />

        <CTABanner
          title="Prueba el modelo por horas sin compromiso"
          description="Te damos crédito al registrarte. Si no te convence el modelo, no has perdido nada — solo recargas lo que quieras."
          primaryLabel="Empezar con crédito gratis"
          primaryHref="/login"
        />
      </article>
    </MarketingLayout>
  )
}